import { useState } from 'react';
import { ArrowLeft, ArrowRight, LayoutDashboard, Building2, Users, FileText, FolderOpen, Check } from 'lucide-react';
import './styles/PlatformConfiguration.css';

interface Props {
  nextStep: () => void;
  previousStep: () => void;
}

interface Submodule {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
}

const SUBMODULES: Submodule[] = [
  {
    id: 'dashboard',
    name: 'Dashboard',
    description: 'Governance overview with KPIs and summary widgets for committees, meetings, policies, and decisions',
    icon: <LayoutDashboard size={18} />,
    iconBg: '#e0f2fe',
    iconColor: '#0284c7',
  },
  {
    id: 'company-profile',
    name: 'Company Profile',
    description: 'Organization identity, logo, vision, mission, and governance frameworks',
    icon: <Building2 size={18} />,
    iconBg: '#f3e8ff',
    iconColor: '#7c3aed',
  },
  {
    id: 'board-committees',
    name: 'Board & Committees',
    description: 'Manage board meetings, agendas, decisions, and action items',
    icon: <Users size={18} />,
    iconBg: '#e0f2fe',
    iconColor: '#0284c7',
  },
  {
    id: 'policies-procedures',
    name: 'Policies & Procedures',
    description: 'Policy lifecycle management, approvals, and acknowledgements',
    icon: <FileText size={18} />,
    iconBg: '#d1fae5',
    iconColor: '#059669',
  },
  {
    id: 'evidence-center',
    name: 'Evidence Center',
    description: 'Centralized repository for all governance documentation, attachments, and supporting files',
    icon: <FolderOpen size={18} />,
    iconBg: '#fce7f3',
    iconColor: '#db2777',
  },
];

const GovernanceSubmodules = ({ nextStep, previousStep }: Props) => {
  const [enabled, setEnabled] = useState<Record<string, boolean>>(Object.fromEntries(SUBMODULES.map((s) => [s.id, true])));

  const toggle = (id: string) => {
    setEnabled((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="gs-card">
      {/* Header */}
      <div className="gs-header">
        <h2 className="gs-title">Governance Submodules</h2>
        <p className="gs-subtitle">Choose which governance submodules to activate</p>
        <p className="gs-desc">Select which governance submodules to activate. Each enabled submodule will have its own setup step.</p>
      </div>

      {/* Submodule list */}
      <div className="gs-list">
        {SUBMODULES.map((mod) => {
          const isOn = enabled[mod.id];
          return (
            <button key={mod.id} type="button" className={`gs-item ${isOn ? 'gs-item-active' : ''}`} onClick={() => toggle(mod.id)}>
              <div className="gs-item-left">
                <div
                  className="gs-item-icon"
                  style={{
                    backgroundColor: mod.iconBg,
                    color: mod.iconColor,
                  }}
                >
                  {mod.icon}
                </div>
                <div className="gs-item-text">
                  <span className="gs-item-name">{mod.name}</span>
                  <span className="gs-item-desc">{mod.description}</span>
                </div>
              </div>
              <div className={`gs-check ${isOn ? 'gs-check-on' : 'gs-check-off'}`}>{isOn && <Check size={14} strokeWidth={3} />}</div>
            </button>
          );
        })}
      </div>

      {/* Footer */}
      <div className="gs-footer">
        <button type="button" onClick={previousStep} className="gs-back-btn">
          <ArrowLeft size={16} />
          Back
        </button>
        <button type="button" onClick={nextStep} className="gs-next-btn">
          Next
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default GovernanceSubmodules;
